/**
 * Admin — Learn topics (the Learn-menu navigation list) + lessons per topic.
 * Topics live in db.learn_topics and drive /api/learn/subjects with no deploy.
 * A topic can carry more than one lesson (db.learn_content, ordered).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { api } from "@/App";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, ChevronUp, ChevronDown, BookOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const AdminTopicsPage = () => {
  const [topics, setTopics] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [lessonsFor, setLessonsFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTopics((await api.get("/admin/learn-topics")).topics || []); }
    catch { toast.error("Could not load topics"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); api.get("/subjects").then((d) => setSubjects(d.subjects || [])).catch(() => {}); }, [load]);

  const subjectName = (sid) => subjects.find((s) => s.id === sid)?.name || sid;
  const bySubject = subjects.map((s) => ({ ...s, rows: topics.filter((t) => t.subject_id === s.id) }))
    .filter((s) => s.rows.length || true);

  const togglePublished = async (t) => {
    try { await axios.patch(`${BACKEND_URL}/api/admin/learn-topics/${t.id}`, { published: !t.published }, { withCredentials: true }); load(); }
    catch { toast.error("Update failed"); }
  };

  const move = async (rows, i, dir) => {
    const j = i + dir; if (j < 0 || j >= rows.length) return;
    const ids = rows.map((r) => r.id); [ids[i], ids[j]] = [ids[j], ids[i]];
    try { await api.post(`/admin/learn-topics/reorder`, { ids }); load(); }
    catch { toast.error("Reorder failed"); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete topic "${t.name}"? (lessons are not deleted)`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/admin/learn-topics/${t.id}`, { withCredentials: true }); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-topics-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Learn topics</h2>
          <p className="text-sm text-muted-foreground">{topics.length} topics drive the Learn menu. Add, reorder, publish — no deploy needed.</p>
        </div>
        <button data-testid="admin-create-topic-btn" onClick={() => setEditing({ subject_id: subjects[0]?.id || "oral_medicine", name: "", topic_id: "", published: true })} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium">
          <Plus className="w-4 h-4" /> New topic
        </button>
      </div>

      {loading && <p className="text-muted-foreground">Loading…</p>}

      <div className="space-y-6">
        {bySubject.map((s) => (
          <div key={s.id} className="border border-border rounded-2xl overflow-hidden bg-card" data-testid={`subject-block-${s.id}`}>
            <div className="px-4 py-3 bg-secondary/40 font-medium text-sm flex items-center justify-between">
              <span>{s.name} <span className="text-muted-foreground">· {s.rows.length}</span></span>
            </div>
            {s.rows.length === 0 && <div className="px-4 py-3 text-sm text-muted-foreground">No topics.</div>}
            {s.rows.map((t, i) => (
              <div key={t.id} className="px-4 py-3 border-t border-border flex items-center gap-3" data-testid={`topic-row-${t.topic_id}`}>
                <div className="flex flex-col">
                  <button onClick={() => move(s.rows, i, -1)} disabled={i === 0} className="p-0.5 disabled:opacity-30 hover:text-primary"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => move(s.rows, i, 1)} disabled={i === s.rows.length - 1} className="p-0.5 disabled:opacity-30 hover:text-primary"><ChevronDown className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{t.name}</div>
                  <div className="text-xs text-muted-foreground">/{t.topic_id}</div>
                </div>
                {t.free_preview && <span className="text-[10px] px-2 py-0.5 rounded-full bg-success/15 text-success font-medium" data-testid={`topic-freepreview-badge-${t.topic_id}`}>Free preview</span>}
                <label className="flex items-center gap-1 text-xs"><input type="checkbox" data-testid={`topic-pub-${t.topic_id}`} checked={t.published !== false} onChange={() => togglePublished(t)} /> published</label>
                <button onClick={() => setLessonsFor(t)} data-testid={`topic-lessons-${t.topic_id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border text-xs hover:bg-secondary"><BookOpen className="w-3.5 h-3.5" /> Lessons</button>
                <button onClick={() => setEditing(t)} data-testid={`edit-topic-${t.topic_id}`} className="p-1.5 rounded hover:bg-secondary"><Edit2 className="w-4 h-4 text-muted-foreground" /></button>
                <button onClick={() => del(t)} className="p-1.5 rounded hover:bg-destructive/15"><Trash2 className="w-4 h-4 text-destructive" /></button>
              </div>
            ))}
          </div>
        ))}
      </div>

      {editing && <TopicModal topic={editing} subjects={subjects} subjectName={subjectName} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {lessonsFor && <LessonsModal topic={lessonsFor} onClose={() => setLessonsFor(null)} />}
    </div>
  );
};

const TopicModal = ({ topic, subjects, onClose, onSaved }) => {
  const isNew = !topic.id;
  const [form, setForm] = useState({ ...topic });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/learn-topics", { subject_id: form.subject_id, name: form.name, topic_id: form.topic_id || undefined, published: form.published, free_preview: !!form.free_preview });
      } else {
        await axios.patch(`${BACKEND_URL}/api/admin/learn-topics/${topic.id}`, { name: form.name, published: form.published, free_preview: !!form.free_preview }, { withCredentials: true });
      }
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="topic-edit-modal">
      <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-semibold">{isNew ? "New topic" : "Edit topic"}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-secondary"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-3">
          <input data-testid="topic-name" required placeholder="Topic name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          <select data-testid="topic-subject" disabled={!isNew} value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm disabled:opacity-60">
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {isNew && <input data-testid="topic-id" placeholder="topic_id (optional, auto from name)" value={form.topic_id} onChange={(e) => setForm({ ...form, topic_id: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm font-mono" />}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="topic-published" checked={form.published !== false} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published (visible in Learn menu)</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="topic-free-preview" checked={!!form.free_preview} onChange={(e) => setForm({ ...form, free_preview: e.target.checked })} /> Free preview (free users can open the full lesson)</label>
          <div className="flex gap-2 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 rounded-lg border border-border text-sm">Cancel</button>
            <button type="submit" data-testid="topic-save" disabled={saving} className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-50">{saving ? "Saving…" : (isNew ? "Create" : "Save")}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const LessonsModal = ({ topic, onClose }) => {
  const [lessons, setLessons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setLessons((await api.get(`/admin/learn-lessons?subject_id=${topic.subject_id}&topic_id=${topic.topic_id}`)).lessons || []); }
    catch { toast.error("Could not load lessons"); }
    finally { setLoading(false); }
  }, [topic]);
  useEffect(() => { load(); }, [load]);

  const del = async (l) => {
    if (!window.confirm(`Delete lesson "${l.name}"?`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/admin/learn-lessons/${l.draft_id}`, { withCredentials: true }); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="lessons-modal">
      <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-semibold">Lessons — {topic.name}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-secondary"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          {!editing && (
            <>
              <div className="flex justify-end">
                <button data-testid="lesson-new" onClick={() => setEditing({ name: "", slug: "", order: lessons.length, estimated_time: 15, sections_json: "[]", takeaways: "", tips: "" })} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm"><Plus className="w-4 h-4" /> Add lesson</button>
              </div>
              {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
              {!loading && lessons.length === 0 && <p className="text-sm text-muted-foreground">No lessons yet.</p>}
              {lessons.map((l) => (
                <div key={l.draft_id} className="p-3 rounded-lg border border-border flex items-center justify-between gap-2" data-testid={`lesson-row-${l.slug || l.draft_id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{l.name}</div>
                    <div className="text-xs text-muted-foreground">order {l.order ?? 0} · /{l.slug || l.draft_id} · {(l.sections || []).length} sections</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditing({ ...l, sections_json: JSON.stringify(l.sections || [], null, 2), takeaways: (l.key_takeaways || []).join("\n"), tips: (l.exam_tips || []).join("\n") })} className="p-1.5 rounded hover:bg-secondary"><Edit2 className="w-4 h-4 text-muted-foreground" /></button>
                    <button onClick={() => del(l)} className="p-1.5 rounded hover:bg-destructive/15"><Trash2 className="w-4 h-4 text-destructive" /></button>
                  </div>
                </div>
              ))}
            </>
          )}
          {editing && <LessonForm topic={topic} lesson={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
      </div>
    </div>
  );
};

const LessonForm = ({ topic, lesson, onCancel, onSaved }) => {
  const isNew = !lesson.draft_id;
  const [form, setForm] = useState({ ...lesson });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    let sections;
    try { sections = JSON.parse(form.sections_json || "[]"); }
    catch { return toast.error("Sections JSON is invalid"); }
    setSaving(true);
    const payload = {
      name: form.name, slug: form.slug || undefined, order: Number(form.order) || 0,
      estimated_time: Number(form.estimated_time) || 15, sections,
      key_takeaways: (form.takeaways || "").split("\n").map((s) => s.trim()).filter(Boolean),
      exam_tips: (form.tips || "").split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) await api.post("/admin/learn-lessons", { subject_id: topic.subject_id, topic_id: topic.topic_id, ...payload });
      else await axios.patch(`${BACKEND_URL}/api/admin/learn-lessons/${lesson.draft_id}`, payload, { withCredentials: true });
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-3" data-testid="lesson-form">
      <input data-testid="lesson-name" required placeholder="Lesson name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
      <div className="grid grid-cols-3 gap-2">
        <input data-testid="lesson-slug" placeholder="slug (optional)" value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="px-3 py-2 rounded-lg bg-background border border-border text-sm font-mono" />
        <input data-testid="lesson-order" type="number" placeholder="order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        <input type="number" placeholder="minutes" value={form.estimated_time} onChange={(e) => setForm({ ...form, estimated_time: e.target.value })} className="px-3 py-2 rounded-lg bg-background border border-border text-sm" />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Sections (JSON array of {"{title, content}"})</label>
        <textarea data-testid="lesson-sections" rows={8} value={form.sections_json} onChange={(e) => setForm({ ...form, sections_json: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-xs font-mono" />
      </div>
      <textarea placeholder="Key takeaways (one per line)" rows={2} value={form.takeaways} onChange={(e) => setForm({ ...form, takeaways: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
      <textarea placeholder="Exam tips (one per line)" rows={2} value={form.tips} onChange={(e) => setForm({ ...form, tips: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
      <div className="flex gap-2 pt-2 border-t border-border">
        <button type="button" onClick={onCancel} className="flex-1 px-3 py-2 rounded-lg border border-border text-sm">Cancel</button>
        <button type="submit" data-testid="lesson-save" disabled={saving} className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-50">{saving ? "Saving…" : "Save lesson"}</button>
      </div>
    </form>
  );
};

export default AdminTopicsPage;
